import moment from 'moment-timezone';
import { useMemo } from 'react';
import Dotdotdot from 'react-dotdotdot';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { ButtonInLink } from '@hivebrite/design-system/ButtonInLink';

import { getSalaryValue } from 'hb-react/frontoffice/apps/Jobs/utils/salary';
import AboutScreenReader from 'hb-react/frontoffice/apps/LiveFeed/components/AboutScreenReader';
import { useLandingContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LandingContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import Columns, { Column } from 'hb-react/shared/components/Columns';
import decodeString from 'hb-react/shared/utils/decodeString';

import Landing from '../Landing';

const Line = ({ label, value }) => (
  <Columns mt={2} flexDirection="row" alignItems="center" space={0}>
    <Column width={{ _: 1 / 3, m: 1 / 4 }}>
      <Box fontWeight={1}>
        <Dotdotdot clamp={1} useNativeClamp>
          {label}
        </Dotdotdot>
      </Box>
    </Column>
    <Column width={{ _: 2 / 3, m: 3 / 4 }} ml={4}>
      <Dotdotdot clamp={1} useNativeClamp>
        {value}
      </Dotdotdot>
    </Column>
  </Columns>
);

const Opportunity = () => {
  const {
    currency,
    companyName,
    contractTypeName,
    dailySalary,
    duration,
    experienceTypeName,
    id,
    location,
    logoUrl,
    owner: { name },
    monthlySalary,
    title,
    startDate,
    yearlySalary,
  } = useLandingContext();
  const { currentNetwork } = useDefaultAppContexts();

  const salaryValue = useMemo(
    () =>
      getSalaryValue({
        currency: currency?.display,
        yearlySalary,
        monthlySalary,
        dailySalary,
      }),
    [currency, yearlySalary, monthlySalary, dailySalary],
  );

  const commentsEnabled =
    currentNetwork?.network_configuration?.business_opportunities
      ?.comments_enabled;

  return (
    <Landing
      title={s__('LiveFeed|created an opportunity %{title}').i__({
        owner: name,
        title,
      })}
      commentsEnabled={commentsEnabled}
    >
      <Wrapper>
        <Box alignItems="center" mb={3}>
          {logoUrl && (
            <Box width={52} height={52} mr={4}>
              <img src={logoUrl} alt="" />
            </Box>
          )}
          <Box display="block" flex={1}>
            <Box font="h4-semibold">{decodeString(title)}</Box>
            {companyName && <Box mt={1}>{decodeString(companyName)}</Box>}
          </Box>
        </Box>
        {location?.address && (
          <Line
            label={I18n.t('web.shared.location.location')}
            value={location?.address}
          />
        )}
        {contractTypeName && (
          <Line
            label={I18n.t('web.business_opportunities.type_of_contract')}
            value={I18n.t(contractTypeName)}
          />
        )}
        {salaryValue && (
          <Line
            label={I18n.t('web.business_opportunities.offer')}
            value={salaryValue}
          />
        )}
        {experienceTypeName && (
          <Line
            label={I18n.t('web.business_opportunities.experience')}
            value={I18n.t(experienceTypeName)}
          />
        )}
        {startDate && (
          <Line
            label={I18n.t('web.business_opportunities.form.start_date')}
            value={moment(startDate).format('ll')}
          />
        )}
        {duration && (
          <Line
            label={I18n.t('web.business_opportunities.duration')}
            value={duration}
          />
        )}
        <Box mt={3}>
          <ButtonInLink
            linkProps={{
              to: Routes.opportunity_path(id),
            }}
          >
            {__('Find out more')}
            <AboutScreenReader name={title} />
          </ButtonInLink>
        </Box>
      </Wrapper>
    </Landing>
  );
};

export default Opportunity;

const Wrapper = styled(Box).attrs(() => ({
  bg: 'smoke',
  display: 'block',
  mt: 4,
  mx: { _: -4, m: 0 },
  p: 4,
  pb: 6,
}))`
  border-radius: 2px;
`;
